import React, { useState, useEffect, useCallback } from 'react';
import { MenuItem, Typography, IconButton, Tabs, Tab } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  GrupoAcesso,
  Perfil,
  ParticipanteResponse,
  ParticipantesFilter,
} from 'common/genericTypes';
import { MdSpace, MoreOptionsMenu } from 'm2m.components';
import { Attachment } from 'components/uploadDocs';
import { useGenericStyles } from 'common/genericStyles';
import { Cancel, CheckCircle, Close, Description, Edit } from '@material-ui/icons';
import { RootState } from 'store';
import { componentsPropsValues, localizedTextsMap, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import {
  DataGridPro,
  GridCellParams,
  GridValueGetterParams,
  GridSortModel,
  GridFilterModel,
  getGridStringOperators,
  GridFilterOperator,
  GridValidRowModel,
  GridFilterInputValue,
} from '@mui/x-data-grid-pro';
import { addCpfCnpjMask } from 'utils/mask';
import { JustificativaAnexoDrawer } from 'components/justificativaAnexoDrawer';
import { statusFormularioDescriptions } from 'common/genericDescriptions';

enum DrawerAction {
  Suspender,
  Liberar
}

const Participantes: React.FC = () => {
  const classes = useGenericStyles();
  const history = useHistory();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [pageCount, setPageCount] = useState<number>(0);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [drawerAction, setDrawerAction] = useState<DrawerAction>(DrawerAction.Liberar);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const dispatch = useDispatch();
  const [linkOperator, setLinkOperator] = useState<any>('AND');
  const [participantesFilter, setParticipantesFilter] = useState<Array<ParticipantesFilter>>([]);
  const [closeStatusToast, setCloseStatusToast] = useState<boolean>(false);
  const [participanteId, setParticipanteId] = useState<string>('');

  const isUsuarioOperacoes = useSelector(
    (state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.Operacoes
  );

  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);

  const isUsuarioCadastroOperacoes =
    useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.CadastroOperacoes;

  const {
    postParticipantePaginated: postParticipantePaginated,
    postSuspenderParticipante,
    postLiberarParticipante,
  } = useSelector((state: RootState) => state.participanteApi);

  const [participantes, setParticipantes] = useState<ParticipanteResponse[]>([]);

  const handleCloseDrawer = () => {
    setParticipanteId('');
    setOpenDrawer(false);
  };

  const handleOpenDrawer = (participante: ParticipanteResponse, action: DrawerAction) => {
    setCloseStatusToast(false);
    setParticipanteId(participante.id);
    setDrawerAction(action);
    setOpenDrawer(true);
  };

  const getParticipantes = useCallback(async () => {
    try {
      setShowLoader(true);
      const result = await postParticipantePaginated(
        dispatch,
        { linkOperator: linkOperator, page: page + 1, pageSize, FilterTypeParticipante: participantesFilter },
        true
      );
      setParticipantes(result.result);
      setPageCount(Math.ceil(result.totalCount / result.pageSize));
      setShowLoader(false);
    } catch {}
  }, [participantesFilter, pageSize, page, linkOperator]);

  const submitSuspender = useCallback(
    async (justificativa: string, anexos: Array<Attachment>) => {
      try {
        const files = anexos.map((anexo) => anexo.file!);
        await postSuspenderParticipante(dispatch, participanteId, justificativa, files, true);
        setCloseStatusToast(true);
        await getParticipantes();
      } catch {}
    },
    [dispatch, getParticipantes, postSuspenderParticipante, participanteId]
  );

  const submitLiberar = useCallback(
    async (justificativa: string, anexos: Array<Attachment>) => {
      try {
        const files = anexos.map((anexo) => anexo.file!);
        await postLiberarParticipante(dispatch, participanteId, justificativa, files, true);
        setCloseStatusToast(true);
        await getParticipantes();
      } catch {}
    },
    [dispatch, getParticipantes, postLiberarParticipante, participanteId]
  );
  
  const onFilterChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (!filterModel.items) {
        setParticipantesFilter([]);
        return;
      }
      setLinkOperator(filterModel?.linkOperator?.toString()?.toUpperCase() ?? 'AND');
      var participanteFilter = new Array<ParticipantesFilter>();

      filterModel.items.forEach((filter) => {
        let operatorValue =
          filter.operatorValue == 'startsWith'
            ? 'STARTS_WITH'
            : filter.operatorValue == 'endsWith'
            ? 'ENDS_WITH'
            : filter.operatorValue?.toUpperCase();

        let formattedValue = filter.value?.toString();
        if (formattedValue) {
          let params = {
            columnType: filter.columnField.toUpperCase(),
            operationFilterType: operatorValue,
            value: formattedValue,
          } as ParticipantesFilter;

          participanteFilter.push(params);
        }
      });

      setParticipantesFilter(participanteFilter);
    },
    [getParticipantes, page, pageSize, linkOperator, participantesFilter]
  );
  
  const onSortChange = useCallback(
    (filterModel: GridSortModel) => {
      if (filterModel == null) return;

      var filter = filterModel[0];
      let params = {
        columnType: filter.field.toUpperCase(),
        orderByType: filter.sort?.toUpperCase(),
      } as ParticipantesFilter;

      var newParticipanteFilter = participantesFilter.filter(
        (x) => x.orderByType != 'ASC' && x.orderByType != 'DESC'
      ) as Array<ParticipantesFilter>;
      newParticipanteFilter.push(params);
      setParticipantesFilter([...newParticipanteFilter]);
    },
    [getParticipantes, page, pageSize, participantesFilter]
  );


  const onDetalhesFunction = (participante: ParticipanteResponse) => {
    return history.push(`/gerenciar/participantes/detalhes/${participante.id}`);
  };

  const onSuspenderClick = (participante: ParticipanteResponse) => {
    handleOpenDrawer(participante, DrawerAction.Suspender);
  }

  const onLiberarClick = (participante: ParticipanteResponse) => {
    handleOpenDrawer(participante, DrawerAction.Liberar);
  }

  const onParticipanteEditClick = (participante: ParticipanteResponse) => history.push(`/gerenciar/participantes/editar/${participante.id}`);

  const onParticipanteHistoricoClick = (participante: ParticipanteResponse) =>
    history.push(`/gerenciar/participantes/detalhes/${participante.id}`, { initialTab: 3 });
  
    const getStatusToastByAction = (action: DrawerAction): React.ReactNode => {
      let divClassName: string = '';
      let resultDescription: string = '';
      let icon: React.ReactNode | null = null;
      switch (action) {
        case DrawerAction.Suspender:
          divClassName = classes.errorToast;
          resultDescription = 'suspenso';
          icon = <Cancel style={{ marginRight: 10 }} />;
          break;
        case DrawerAction.Liberar:
          divClassName = classes.successToast;
          resultDescription = 'liberado';
          icon = <CheckCircle style={{ marginRight: 10 }} />;
          break;
      }
      return (
        <div className={divClassName}>
          {icon}
          <div style={{ display: 'flex', flex: 1 }}>
            <Typography variant="body2">{'Participante'}</Typography>&nbsp;<Typography>{resultDescription}</Typography>
            &nbsp;
            <Typography variant="body2">{'com sucesso'}</Typography>
          </div>
          <IconButton onClick={() => setCloseStatusToast(false)}>
            <Close />
          </IconButton>
        </div>
      );
    };

  useEffect(() => {
    getParticipantes();
  }, [
    getParticipantes, page, participantesFilter]);

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ['equals', 'contains', 'startsWith', 'endsWith'].includes(value)
  );
  
  const notOperator: GridFilterOperator<GridValidRowModel, string> = {
    label: 'not',
    value: 'not',
    getApplyFilterFn: () => null,
    InputComponent: GridFilterInputValue
  };

  const getGridColumns = useCallback(
    () => [
      {
        field: 'STATUS',
        headerName: 'Status',
        type: 'string',
        filterOperators: [...filterOperators, notOperator],
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const participante = params.row as ParticipanteResponse;
          return participante.registro.status;
        },
        renderCell: (params: GridCellParams) => {
          const participante = params.row as ParticipanteResponse;
          return statusFormularioDescriptions[participante.registro.status];
        },
        width: 300,
        minWidth: 150,
        maxWidth: 350,
      },
      {
        field: 'TICKET',
        headerName: 'Ticket',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const participante = params.row as ParticipanteResponse;
          return participante.ticket;
        },
        width: 300,
        minWidth: 150,
        maxWidth: 350,
      },
      {
        field: 'CNPJ',
        headerName: 'CNPJ',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const participante = params.row as ParticipanteResponse;
          return addCpfCnpjMask(participante.cnpj);
        },
        width: 300,
        minWidth: 150,
        maxWidth: 350,
      },      
      {
        field: 'RAZAO_SOCIAL',
        headerName: 'Razão Social',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const participante = params.row as ParticipanteResponse;
          return participante.razaoSocial;
        },
        width: 350,
        minWidth: 150,
        maxWidth: 350,
      },
      {
        field: 'FUNCAO',
        headerName: 'Funções',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const participante = params.row as ParticipanteResponse;
          return participante.funcoes.map((pf) => pf.funcao).join(', ');
        },
        width: 330,
        minWidth: 150,
        maxWidth: 350,
      },
      {
        field: 'actions',
        headerName: '',
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params: GridCellParams) => (
          <MoreOptionsMenu>
            {onDetalhesFunction && (
              <MenuItem
                onClick={() => {
                  onDetalhesFunction(params.row as ParticipanteResponse);
                }}
              >
                <Description fontSize="small" style={{ marginRight: '10px' }} />
                {'Ver Detalhes'}
              </MenuItem>
            )}

            <MenuItem onClick={ () => onParticipanteHistoricoClick(params.row as ParticipanteResponse)}>
                <InsertDriveFileIcon fontSize="small" style={{ marginRight: '10px' }} />
                {'Ver justificativas'}
            </MenuItem>
            
            {(isUsuarioOperacoes || isUsuarioCadastroOperacoes) && !isUsuarioLeitura && (
              <MenuItem
                onClick={() => {
                  onParticipanteEditClick(params.row as ParticipanteResponse);
                }}
              >
                <Edit fontSize="small" style={{ marginRight: '10px' }} />
                {'Editar'}
              </MenuItem>
            )}

            {isUsuarioOperacoes && !isUsuarioLeitura && (
              <>
                {params.row.suspenso ? (
                  <MenuItem onClick={() => onLiberarClick(params.row as ParticipanteResponse)}>
                    <CheckCircle fontSize="small" style={{ marginRight: '10px' }} /> {'Liberar'}
                  </MenuItem>
                ) : (
                  <MenuItem onClick={() => onSuspenderClick(params.row as ParticipanteResponse)}>
                    <Cancel fontSize="small" style={{ marginRight: '10px' }} /> {'Suspender'}
                  </MenuItem>
                )}
              </>
            )}
          </MoreOptionsMenu>
        ),
        width: 75,
        minWidth: 75,
        maxWidth: 200,
      },
    ],
    []
  );
  return (
    <div>
      {closeStatusToast && getStatusToastByAction(drawerAction)}
      <MdSpace />
      <DataGridPro
        autoHeight
        localeText={localizedTextsMap}
        componentsProps={componentsPropsValues}
        rows={participantes}
        columns={getGridColumns()}
        components={{
          Toolbar: CustomToolBarUtils,
        }}
        disableSelectionOnClick
        rowsPerPageOptions={rowsPerPageOptionsValues}
        page={page}
        rowCount={pageCount}
        pagination
        paginationMode="server"
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize), setPage(0);
        }}
        pageSize={pageSize}
        loading={showLoader}
        filterMode="server"
        onFilterModelChange={onFilterChange}
        sortingMode="server"
        onSortModelChange={onSortChange}
      />
      <MdSpace />

      <JustificativaAnexoDrawer
        open={openDrawer}
        onClose={handleCloseDrawer}
        onSubmit={drawerAction === DrawerAction.Suspender ? submitSuspender : submitLiberar}
        justificativaObrigatoria
        overrideTitle={
          drawerAction === DrawerAction.Suspender ? 'Justificativa da suspensão' : 'Justificativa da liberação'
        }
        overrideSubmitButtonText={drawerAction === DrawerAction.Suspender ? 'Suspender' : 'Liberar'}
        overrideSubmitButtonColor={drawerAction === DrawerAction.Suspender ? 'error' : 'primary'}
      />
    </div>
  );
};

export default Participantes;
